// src/admin/pages/CohortDetail.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { adminService } from "../../services/adminService";
import { Loader2 } from "lucide-react";

const CohortDetail = () => {
  const { cohortId } = useParams();

  const [cohort, setCohort] = useState(null);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [courseTitle, setCourseTitle] = useState("");
  const [loading, setLoading] = useState(true);

  const [enrollUserId, setEnrollUserId] = useState("");
  const [search, setSearch] = useState("");

  const loadDetails = useCallback(async () => {
    setLoading(true);
    try {
      const [cohortData, userData, courseData, allUserData] =
        await Promise.all([
          adminService.getCohortDetail(cohortId),
          adminService.getCohortUsers(cohortId),
          adminService.getCourses(),
          adminService.getUsers(),
        ]);

      setCohort(cohortData);
      setUsers(userData);
      setAllUsers(allUserData);
      const courseMatch = courseData.find(
        (course) => String(course.id) === String(cohortData.course_id)
      );
      setCourseTitle(courseMatch?.title || "");
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const enroll = async () => {
    if (!enrollUserId) return;
    await adminService.enrollIntoCohort(cohortId, enrollUserId);

    const updated = await adminService.getCohortUsers(cohortId);
    setUsers(updated);

    setEnrollUserId("");
  };

  const remove = async (userId) => {
    await adminService.removeFromCohort(cohortId, userId);
    setUsers(users.filter((u) => u.user_id !== userId));
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return (
        name.toLowerCase().includes(q) ||
        String(u.email || "").toLowerCase().includes(q)
      );
    });
  }, [users, search]);

  const availableUsers = useMemo(() => {
    const enrolledIds = new Set(users.map((u) => u.user_id));
    return allUsers.filter((u) => !enrolledIds.has(u.user_id));
  }, [allUsers, users]);

  /* ===================================================
     RENDER
  =================================================== */

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-[#54B0AF]">
        <Loader2 size={22} className="animate-spin" />
        Loading cohort…
      </div>
    );
  }

  if (!cohort) {
    return <div className="p-6 text-red-600">Cohort not found.</div>;
  }

  return (
    <div className="space-y-8">

      {/* Header */}
      <div>
        <h1 className="text-display">{cohort.name}</h1>

        <div className="mt-2 text-muted text-sm space-y-1">
          <p>
            <span className="font-semibold text-[#323232]">Course:</span>{" "}
            {courseTitle || cohort.course_id}
          </p>
          <p>
            <span className="font-semibold text-[#323232]">Start:</span>{" "}
            {formatDate(cohort.start_date)}
          </p>
          {cohort.status && (
            <p>
              <span className="font-semibold text-[#323232]">Status:</span>{" "}
              <span className="capitalize">{cohort.status}</span>
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Enrolled users" value={users.length} />
        <StatCard label="Start date" value={formatDate(cohort.start_date)} />
        <StatCard label="Course" value={courseTitle || "--"} />
      </div>

      {/* Users + Enroll Section */}
      <div className="bg-white shadow stakeholder-summary-card rounded-lg p-6 space-y-6">

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h2 className="text-title mb-0">Enrolled Users</h2>
          <input
            type="text"
            placeholder="Search enrolled users"
            className="border rounded-md px-3 py-2 text-sm w-full md:w-72"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Users Table */}
        <table className="w-full border-collapse text-[15px]">
          <thead className="bg-gray-50">
            <tr className="border-b">
              <Th>Name</Th>
              <Th>Email</Th>
              <Th></Th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.user_id} className="border-b hover:bg-gray-100 transition">
                <Td>
                  {u.first_name} {u.last_name}
                </Td>
                <Td>{u.email}</Td>

                <Td className="text-right">
                  <button
                    onClick={() => remove(u.user_id)}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Remove
                  </button>
                </Td>
              </tr>
            ))}

            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan="3" className="text-center text-muted italic py-4">
                  No users enrolled in this cohort.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Enroll Section */}
        <div>
          <h3 className="text-title mb-2">Enroll User</h3>

          <div className="flex items-center gap-3">
            <select
              className="border p-3 rounded-md stakeholder-input w-80 text-sm"
              value={enrollUserId}
              onChange={(e) => setEnrollUserId(e.target.value)}
            >
              <option value="">
                {availableUsers.length === 0
                  ? "No available users"
                  : "Select a user"}
              </option>
              {availableUsers.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {`${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                    u.email}{" "}
                  {u.email ? `(${u.email})` : ""}
                </option>
              ))}
            </select>

            <button
              onClick={enroll}
              disabled={!enrollUserId}
              className="px-4 py-2 bg-[#54B0AF] text-white rounded-md shadow hover:opacity-90 transition text-sm"
            >
              Enroll
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CohortDetail;


/* ===================================================
   Styled Subcomponents
=================================================== */

const Th = ({ children }) => (
  <th className="p-2 text-left text-title font-semibold">{children}</th>
);

const Td = ({ children, className = "" }) => (
  <td className={`p-2 ${className}`}>{children}</td>
);

const StatCard = ({ label, value }) => (
  <div className="bg-white shadow rounded-lg p-5 text-center stakeholder-summary-card">
    <p className="text-muted text-sm">{label}</p>
    <p className="text-xl font-semibold text-[#323232] mt-1">{value}</p>
  </div>
);

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString();
};
